<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { Activity, KeyRound, Save, ShieldCheck, UserPlus } from 'lucide-vue-next';
import { apiRequest } from '@/composables/useApi';
import type { UserRole } from '@/types/domain';

interface AdminUser {
  id: string;
  familyId: string | null;
  familyName: string | null;
  role: UserRole;
  firstName: string;
  lastName: string;
  email: string;
  isBlocked: number | boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

interface AdminFamily {
  id: string;
  name: string;
  userCount: number;
  childCount: number;
  createdAt: string;
}

interface SystemStatus {
  status: string;
  storage: { d1: boolean; r2: boolean; kv: boolean };
  counts: {
    users: number;
    families: number;
    activeSessions: number;
    messages: number;
    mediaFiles: number;
  };
}

const users = ref<AdminUser[]>([]);
const families = ref<AdminFamily[]>([]);
const systemStatus = ref<SystemStatus | null>(null);
const selectedUserId = ref('');
const notice = ref('');
const errorText = ref('');
const isLoading = ref(false);

const userForm = ref({
  firstName: '',
  lastName: '',
  email: '',
  role: 'parent' as UserRole,
  familyId: '',
  password: ''
});

const selectedUser = computed(() => users.value.find((user) => user.id === selectedUserId.value) ?? null);

onMounted(() => {
  void loadAdminData();
});

async function loadAdminData() {
  isLoading.value = true;
  errorText.value = '';
  try {
    const [userResponse, familyResponse, statusResponse] = await Promise.all([
      apiRequest<{ users: AdminUser[] }>('/admin/users'),
      apiRequest<{ families: AdminFamily[] }>('/admin/families'),
      apiRequest<SystemStatus>('/admin/system-status')
    ]);
    users.value = userResponse.users;
    families.value = familyResponse.families;
    systemStatus.value = statusResponse;
  } catch {
    errorText.value = 'Admin-Daten konnten nicht geladen werden.';
  } finally {
    isLoading.value = false;
  }
}

function editUser(user: AdminUser) {
  selectedUserId.value = user.id;
  userForm.value = {
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    role: user.role,
    familyId: user.familyId ?? '',
    password: ''
  };
}

function resetForm() {
  selectedUserId.value = '';
  userForm.value = {
    firstName: '',
    lastName: '',
    email: '',
    role: 'parent',
    familyId: families.value[0]?.id ?? '',
    password: ''
  };
}

async function saveUser() {
  notice.value = '';
  errorText.value = '';
  const payload = {
    firstName: userForm.value.firstName,
    lastName: userForm.value.lastName,
    email: userForm.value.email,
    role: userForm.value.role,
    familyId: userForm.value.role === 'admin' ? null : userForm.value.familyId
  };

  try {
    if (selectedUser.value) {
      await apiRequest(`/admin/users/${selectedUser.value.id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      });
      notice.value = 'Benutzer wurde gespeichert.';
    } else {
      await apiRequest('/admin/users', {
        method: 'POST',
        body: JSON.stringify({ ...payload, password: userForm.value.password })
      });
      notice.value = 'Benutzer wurde angelegt.';
    }
    resetForm();
    await loadAdminData();
  } catch {
    errorText.value = 'Benutzer konnte nicht gespeichert werden.';
  }
}

async function setUserActive(user: AdminUser, active: boolean) {
  notice.value = '';
  errorText.value = '';
  try {
    await apiRequest(`/admin/users/${user.id}/status`, {
      method: 'POST',
      body: JSON.stringify({ isBlocked: !active })
    });
    notice.value = active ? 'Benutzer ist aktiv.' : 'Benutzer ist inaktiv.';
    await loadAdminData();
  } catch {
    errorText.value = 'Status konnte nicht geaendert werden.';
  }
}

async function resetPassword(user: AdminUser) {
  notice.value = '';
  errorText.value = '';
  try {
    await apiRequest(`/admin/users/${user.id}/password-reset`, { method: 'POST' });
    notice.value = 'Passwort-Reset wurde vorbereitet. Das Passwort wird nicht angezeigt.';
  } catch {
    errorText.value = 'Passwort-Reset konnte nicht vorbereitet werden.';
  }
}
</script>

<template>
  <section class="mx-auto max-w-7xl px-4 py-6 pb-28 sm:px-6 md:pb-10">
    <div class="flex flex-wrap items-center justify-between gap-4">
      <div>
        <p class="text-sm font-black uppercase tracking-[0.16em] text-heart-leaf">Systemverwaltung</p>
        <h1 class="mt-2 text-4xl font-black text-heart-berry md:text-6xl">Adminbereich</h1>
      </div>
      <button class="focus-ring inline-flex min-h-14 items-center gap-2 rounded-full bg-white px-5 font-black text-heart-ink shadow-sm" type="button" @click="loadAdminData">
        <Activity class="h-6 w-6 text-heart-coral" aria-hidden="true" />
        Aktualisieren
      </button>
    </div>

    <p v-if="notice" class="mt-5 rounded-2xl bg-heart-mint px-4 py-3 font-bold text-heart-leaf">{{ notice }}</p>
    <p v-if="errorText" class="mt-5 rounded-2xl bg-rose-100 px-4 py-3 font-bold text-rose-800">{{ errorText }}</p>

    <div class="mt-8 grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
      <section class="rounded-[28px] bg-white p-6 shadow-soft">
        <div class="flex items-center gap-3">
          <UserPlus class="h-7 w-7 text-heart-coral" aria-hidden="true" />
          <h2 class="text-2xl font-black text-heart-berry">{{ selectedUser ? 'Benutzer bearbeiten' : 'Benutzer anlegen' }}</h2>
        </div>
        <form class="mt-6 grid gap-4 md:grid-cols-2" @submit.prevent="saveUser">
          <label class="block text-sm font-black text-heart-ink">Vorname<input v-model="userForm.firstName" class="focus-ring mt-2 min-h-12 w-full rounded-2xl border border-orange-100 px-4" required /></label>
          <label class="block text-sm font-black text-heart-ink">Nachname<input v-model="userForm.lastName" class="focus-ring mt-2 min-h-12 w-full rounded-2xl border border-orange-100 px-4" required /></label>
          <label class="block text-sm font-black text-heart-ink md:col-span-2">E-Mail<input v-model="userForm.email" class="focus-ring mt-2 min-h-12 w-full rounded-2xl border border-orange-100 px-4" type="email" required /></label>
          <label class="block text-sm font-black text-heart-ink">Rolle
            <select v-model="userForm.role" class="focus-ring mt-2 min-h-12 w-full rounded-2xl border border-orange-100 px-4">
              <option value="admin">admin</option>
              <option value="parent">parent</option>
              <option value="child_profile">child profile</option>
            </select>
          </label>
          <label class="block text-sm font-black text-heart-ink">Familie
            <select v-model="userForm.familyId" class="focus-ring mt-2 min-h-12 w-full rounded-2xl border border-orange-100 px-4" :disabled="userForm.role === 'admin'" :required="userForm.role !== 'admin'">
              <option value="">Keine Familie</option>
              <option v-for="family in families" :key="family.id" :value="family.id">{{ family.name }}</option>
            </select>
          </label>
          <label v-if="!selectedUser" class="block text-sm font-black text-heart-ink md:col-span-2">Startpasswort<input v-model="userForm.password" class="focus-ring mt-2 min-h-12 w-full rounded-2xl border border-orange-100 px-4" type="password" minlength="12" autocomplete="new-password" required /></label>
          <div class="flex flex-wrap gap-3 md:col-span-2">
            <button class="focus-ring inline-flex min-h-12 items-center gap-2 rounded-2xl bg-heart-coral px-5 font-black text-white" type="submit">
              <Save class="h-5 w-5" aria-hidden="true" />
              Speichern
            </button>
            <button class="focus-ring min-h-12 rounded-2xl bg-heart-cream px-5 font-black text-heart-ink" type="button" @click="resetForm">Neu</button>
          </div>
        </form>
      </section>

      <section class="rounded-[28px] bg-white p-6 shadow-soft">
        <div class="flex items-center gap-3">
          <ShieldCheck class="h-7 w-7 text-heart-leaf" aria-hidden="true" />
          <h2 class="text-2xl font-black text-heart-berry">Systemstatus</h2>
        </div>
        <div v-if="systemStatus" class="mt-6 grid gap-3 text-lg font-black text-heart-ink">
          <p class="rounded-2xl bg-heart-cream px-4 py-3">Status: {{ systemStatus.status }}</p>
          <p class="rounded-2xl bg-heart-cream px-4 py-3">Benutzer: {{ systemStatus.counts.users }}</p>
          <p class="rounded-2xl bg-heart-cream px-4 py-3">Familien: {{ systemStatus.counts.families }}</p>
          <p class="rounded-2xl bg-heart-cream px-4 py-3">Aktive Sessions: {{ systemStatus.counts.activeSessions }}</p>
          <p class="rounded-2xl bg-heart-cream px-4 py-3">D1: {{ systemStatus.storage.d1 ? 'verbunden' : 'fehlt' }} · R2: {{ systemStatus.storage.r2 ? 'verbunden' : 'fehlt' }}</p>
        </div>
      </section>
    </div>

    <section class="mt-6 rounded-[28px] bg-white p-6 shadow-soft">
      <h2 class="text-2xl font-black text-heart-berry">Benutzerliste</h2>
      <div class="mt-5 grid gap-3">
        <article v-for="user in users" :key="user.id" class="grid gap-4 rounded-[24px] bg-heart-cream p-4 md:grid-cols-[1fr_auto] md:items-center">
          <div>
            <p class="text-xl font-black text-heart-ink">{{ user.firstName }} {{ user.lastName }}</p>
            <p class="font-bold text-slate-600">{{ user.email }} · {{ user.role }} · {{ user.familyName || 'System' }}</p>
            <p class="mt-1 text-sm font-bold text-slate-500">{{ user.isBlocked ? 'inaktiv' : 'aktiv' }}</p>
          </div>
          <div class="flex flex-wrap gap-2">
            <button class="focus-ring min-h-11 rounded-2xl bg-white px-4 font-black text-heart-ink" type="button" @click="editUser(user)">Bearbeiten</button>
            <button class="focus-ring min-h-11 rounded-2xl bg-white px-4 font-black text-heart-ink" type="button" @click="setUserActive(user, Boolean(user.isBlocked))">
              {{ user.isBlocked ? 'Aktiv setzen' : 'Inaktiv setzen' }}
            </button>
            <button class="focus-ring inline-flex min-h-11 items-center gap-2 rounded-2xl bg-white px-4 font-black text-heart-berry" type="button" @click="resetPassword(user)">
              <KeyRound class="h-4 w-4" aria-hidden="true" />
              Passwort zuruecksetzen
            </button>
          </div>
        </article>
      </div>
    </section>

    <section class="mt-6 rounded-[28px] bg-white p-6 shadow-soft">
      <h2 class="text-2xl font-black text-heart-berry">Familienuebersicht</h2>
      <div class="mt-5 grid gap-3 md:grid-cols-2">
        <article v-for="family in families" :key="family.id" class="rounded-[24px] bg-heart-cream p-4">
          <p class="text-xl font-black text-heart-ink">{{ family.name }}</p>
          <p class="mt-2 font-bold text-slate-600">{{ family.userCount }} Benutzer · {{ family.childCount }} Kinderprofile</p>
          <p class="mt-1 text-sm font-bold text-slate-500">Angelegt: {{ new Date(family.createdAt).toLocaleDateString('de-DE') }}</p>
        </article>
      </div>
    </section>
  </section>
</template>
